import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from 'react';
import './DashboardPage.css';
import { useAuth } from '../context/AuthContext';
import { API, apiRequest } from '../services/api';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { updateAccount } from '../services/UserService';
import HeaderBanner from '../components/HeaderBanner';
import { usePurchaseService } from '../services/PurchaseService';
import { clearStripeCache } from '../utils/stripeCache';
import {
  getDashboardCache,
  setDashboardCache,
  clearDashboardCache,
} from '../services/CacheService';
import { getUserFacingErrorMessage } from '../services/errors';

// Preload frequently used images at module load
const __preloadImgs = (() => {
  const list = ['/SurveyLogo.png', '/OrderLogo.png'];
  list.forEach((src) => {
    const img = new Image();
    try {
      img.decoding = 'async';
      // @ts-ignore
      img.loading = 'eager';
      // @ts-ignore
      img.fetchPriority = 'high';
    } catch {}
    img.src = src;
  });
  return true;
})();

const TABS = {
  ACCOUNT: 'account',
  RESULTS: 'results',
  ORDERS: 'orders',
};

function formatDateTime(date) {
  try {
    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(date);
  } catch {
    return date.toLocaleString();
  }
}

// Ensure surveys are shown most recent first
const getSurveyTime = (r) => {
  const d = r?.submittedAt || r?.createdAt || r?.updatedAt || r?.date;
  const t = d ? new Date(d).getTime() : 0;
  return Number.isFinite(t) ? t : 0;
};
const sortSurveysDesc = (arr = []) =>
  [...arr].sort((a, b) => getSurveyTime(b) - getSurveyTime(a));

const LockIcon = ({ size = 16, color = 'var(--teal-900)' }) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    aria-hidden='true'
  >
    <rect
      x='5'
      y='11'
      width='14'
      height='9'
      rx='2'
      stroke={color}
      strokeWidth='2'
    />
    <path
      d='M8 11V8a4 4 0 1 1 8 0v3'
      stroke={color}
      strokeWidth='2'
      strokeLinecap='round'
    />
  </svg>
);

function LoadingDashboard() {
  return (
    <div className='newdash-page'>
      <HeaderBanner title='Loading your dashboard…' />
      <main className='newdash-panel' role='status' aria-live='polite'>
        <div className='skeleton card'>
          <div className='skeleton-row'>
            <div className='skeleton-avatar' />
            <div className='skeleton-col'>
              <div className='skeleton-line w-60' />
              <div className='skeleton-line w-40' />
            </div>
            <div className='skeleton-btn' />
          </div>
        </div>
        <div className='skeleton card'>
          <div className='skeleton-row'>
            <div className='skeleton-avatar' />
            <div className='skeleton-col'>
              <div className='skeleton-line w-50' />
              <div className='skeleton-line w-30' />
            </div>
            <div className='skeleton-btn' />
          </div>
        </div>
        <div className='skeleton card'>
          <div className='skeleton-row'>
            <div className='skeleton-avatar' />
            <div className='skeleton-col'>
              <div className='skeleton-line w-55' />
              <div className='skeleton-line w-35' />
            </div>
            <div className='skeleton-btn' />
          </div>
        </div>
      </main>
    </div>
  );
}

export default function DashboardPage() {
  const { token, isLoading: authLoading, logout } = useAuth();
  const [activeTab, setActiveTab] = useState(TABS.RESULTS);
  const [accordionOpen, setAccordionOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { getPurchaseBySessionId, checkSurveyUnlockStatus } =
    usePurchaseService();

  // Data state
  const [user, setUser] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Save form state
  const [displayName, setDisplayName] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [saveErr, setSaveErr] = useState('');

  // Post-checkout verification state
  const [verifying, setVerifying] = useState(false);
  const [verifyMsg, setVerifyMsg] = useState('');
  const isPollingRef = useRef(false);

  const fetchAll = useCallback(
    (background = false) => {
      if (!token) return Promise.resolve();
      if (!background) {
        setLoading(true);
        setError(null);
      } else {
        setError(null);
      }
      return Promise.all([
        apiRequest(API.USERS_ME, { token }).then((r) => r.json()),
        apiRequest(API.MY_PURCHASES, { token }).then((r) => r.json()),
        apiRequest(API.MY_SURVEY_RESULTS, { token }).then((r) => r.json()),
      ])
        .then(([userData, myPurchases, mySurveys]) => {
          const sortedSurveys = sortSurveysDesc(mySurveys || []);
          setUser(userData);
          setPurchases(myPurchases || []);
          setSurveys(sortedSurveys);
          setDisplayName(userData?.name || '');
          setEmail(userData?.email || '');
          // Update cache with fresh data (surveys sorted desc)
          setDashboardCache({
            user: userData,
            purchases: myPurchases || [],
            surveys: sortedSurveys,
          });
          if (!background) setLoading(false);
        })
        .catch(() => {
          setError('Failed to load dashboard data.');
          if (!background) setLoading(false);
        });
    },
    [token],
  );

  // Use cache on mount where appropriate
  useEffect(() => {
    if (authLoading || !token) return;
    const hasSessionId = new URLSearchParams(location.search).has('session_id');
    if (hasSessionId) return; // let verification flow run instead

    const cached = getDashboardCache();
    if (cached) {
      setUser(cached.user);
      setPurchases(cached.purchases);
      setSurveys(sortSurveysDesc(cached.surveys || []));
      setDisplayName(cached.user?.name || '');
      setEmail(cached.user?.email || '');
      setLoading(false);
      // Revalidate in background to keep cache accurate (e.g., after payment)
      fetchAll(true);
      return;
    }
    fetchAll();
  }, [token, authLoading, fetchAll, location.search]);

  // Clear cache when user logs out
  useEffect(() => {
    if (!token && !authLoading) {
      clearDashboardCache();
    }
  }, [token, authLoading]);

  // Detect session_id from Stripe redirect and verify/poll backend until unlocked
  useEffect(() => {
    if (!token) return;
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    if (!sessionId) return;

    // We have a Stripe redirect signal; clear any persisted embedded-checkout
    // state so future visits to /payment don't hydrate old credentials.
    clearStripeCache();

    // Prevent starting multiple polling loops
    if (isPollingRef.current) return;
    isPollingRef.current = true;

    let cancelled = false;
    setVerifying(true);
    setVerifyMsg('Processing your payment… This may take a few seconds.');
    setLoading(true);

    const sleep = (ms) => new Promise((res) => setTimeout(res, ms));

    (async () => {
      const delays = [2000, 3000, 4000, 5000, 6000, 8000];
      let unlocked = false;
      for (let i = 0; i < delays.length; i++) {
        if (cancelled) return;
        try {
          console.debug(
            '[Checkout] Poll attempt',
            i + 1,
            'sessionId=',
            sessionId,
          );
          // 1) Hit the session endpoint (existing)
          const status = await getPurchaseBySessionId(sessionId, token);
          console.debug('[Checkout] session status response:', status);

          // Normalize response (some backends return an array)
          const statusObj = Array.isArray(status) ? status[0] : status;
          if (!statusObj) {
            await sleep(delays[i]);
            continue;
          }

          // Try to infer surveyResponseId from status, or from latest surveys list
          const surveyResponseId =
            statusObj?.surveyResponseId || statusObj?.survey?.responseId;

          // 2) If we got surveyResponseId, validate unlock using the new endpoint
          if (surveyResponseId) {
            try {
              const unlock = await checkSurveyUnlockStatus(
                surveyResponseId,
                token,
              );
              console.debug('[Checkout] unlock status:', unlock);
              unlocked = Boolean(unlock?.isUnlocked);
            } catch (e) {
              console.debug(
                '[Checkout] unlock status check failed:',
                e?.message,
              );
            }
          } else {
            // Fallback: if status itself indicates unlocked/paid/completed
            const s = String(statusObj?.status || '').toLowerCase();
            unlocked = Boolean(
              statusObj?.surveyUnlocked ||
              statusObj?.unlocked ||
              s === 'unlocked' ||
              s === 'paid' ||
              s === 'succeeded' ||
              s === 'complete' ||
              s === 'completed' ||
              s === 'success',
            );
          }

          if (unlocked) {
            await fetchAll();
            // Stop showing the banner immediately and clean URL to avoid retriggering
            setVerifying(false);
            setVerifyMsg('');
            isPollingRef.current = false;
            navigate(location.pathname, { replace: true });
            return; // end polling routine
          }
        } catch (e) {
          console.debug('[Checkout] poll error:', e?.message);
        }
        await sleep(delays[i]);
      }
      if (!cancelled) {
        setVerifying(false);
        setVerifyMsg('');
        setLoading(false);
        isPollingRef.current = false;
        // Clean the URL to avoid re-triggering on refresh
        navigate(location.pathname, { replace: true });
      }
    })();

    return () => {
      cancelled = true;
      isPollingRef.current = false;
    };
    // Intentionally exclude service function identities to prevent re-entrancy
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, location.search, fetchAll, navigate, location.pathname]);

  const upperName = (user?.name || '').toUpperCase();
  const wordmark =
    activeTab === TABS.RESULTS
      ? upperName
        ? `${upperName}'S TEST RESULTS`
        : 'MY TEST RESULTS'
      : upperName || 'ACCOUNT';

  const handleSave = async (e) => {
    e.preventDefault();
    setSaveErr('');
    setSaveMsg('');

    // Validate password confirmation
    if (password && password !== confirmPassword) {
      setSaveErr('Passwords do not match.');
      return;
    }

    // Detect what changed (used for post-save behavior only)
    const changingEmail = email && user?.email !== email;
    const changingName = displayName && user?.name !== displayName;

    // Require current password for any save
    if (!currentPassword) {
      setSaveErr('Please enter your current password to update your account.');
      return;
    }

    const payload = {
      currentPassword,
      // Always send all three fields; if not changing password, reuse currentPassword
      newPassword: password || currentPassword,
      newEmail: email,
      newName: displayName,
    };

    setSaveLoading(true);
    try {
      await updateAccount(payload, token);

      if (changingEmail) {
        // Email is the login identifier; require re-login
        logout();
        clearDashboardCache(); // ensure fresh fetch on next login
        navigate('/', { state: { openLogin: true, email } });
        return;
      }

      // Refresh user profile after update for non-email changes
      const refreshed = await apiRequest(API.USERS_ME, { token }).then((r) =>
        r.json(),
      );
      setUser(refreshed);
      setDisplayName(refreshed?.name || displayName);
      setEmail(refreshed?.email || email);
      setSaveMsg('Account updated successfully.');
      // Update cache with latest user (reuse previous purchases/surveys)
      setDashboardCache({ user: refreshed, purchases, surveys });
      // Clear sensitive fields
      setCurrentPassword('');
      setPassword('');
      setConfirmPassword('');
    } catch (err) {
      setSaveErr(getUserFacingErrorMessage(err, 'access'));
    } finally {
      setSaveLoading(false);
    }
  };

  const downloadPdf = (item) => {
    const { pdfContent, pdfFileName } = item || {};
    if (!pdfContent) return;
    // If pdfContent is a URL, open it; if base64, decode to Blob
    if (/^https?:\/\//i.test(pdfContent)) {
      window.open(pdfContent, '_blank', 'noopener,noreferrer');
      return;
    }
    try {
      let base64 = pdfContent;
      if (pdfContent.startsWith('data:application/pdf;base64,')) {
        base64 = pdfContent.split(',')[1];
      }
      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = pdfFileName || 'report.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Failed to download PDF', e);
    }
  };

  if (authLoading)
    return (
      <div className='newdash-page'>
        <HeaderBanner title='Loading…' />
      </div>
    );
  if (!token && !authLoading)
    return (
      <div className='newdash-page'>
        <HeaderBanner title='Please log in.' />
      </div>
    );

  // Show a full loading page while auth, data fetch, or purchase verification is in progress
  if (authLoading || loading || verifying) {
    return <LoadingDashboard />;
  }

  return (
    <div className='newdash-page'>
      <HeaderBanner title={wordmark} />

      {verifying && loading && (
        <div
          style={{
            maxWidth: 'var(--container-max)',
            margin: '12px auto 0',
            padding: '12px 16px',
            background: '#fff3cd',
            border: '1px solid #ffeeba',
            color: '#856404',
            borderRadius: '6px',
          }}
        >
          {verifyMsg || 'Processing your payment…'}
        </div>
      )}

      <main className='newdash-panel' role='main'>
        {loading && (
          <div
            style={{
              padding: '24px 32px',
              color: 'var(--slate)',
              fontFamily: 'var(--ui)',
            }}
          >
            Loading dashboard data...
          </div>
        )}
        {error && (
          <div
            style={{
              padding: '24px 32px',
              color: 'var(--slate)',
              fontFamily: 'var(--ui)',
            }}
          >
            {error}
          </div>
        )}

        {!loading && !error && (
          <>
            <div
              className='tabs'
              role='tablist'
              aria-label='Dashboard sections'
            >
              <button
                className='tab'
                role='tab'
                aria-selected={activeTab === TABS.ACCOUNT}
                aria-controls='panel-account'
                id='tab-account'
                onClick={() => setActiveTab(TABS.ACCOUNT)}
              >
                Account Details
              </button>
              <button
                className='tab'
                role='tab'
                aria-selected={activeTab === TABS.RESULTS}
                aria-controls='panel-results'
                id='tab-results'
                onClick={() => setActiveTab(TABS.RESULTS)}
              >
                My Test Results
              </button>
              <button
                className='tab'
                role='tab'
                aria-selected={activeTab === TABS.ORDERS}
                aria-controls='panel-orders'
                id='tab-orders'
                onClick={() => setActiveTab(TABS.ORDERS)}
              >
                Orders
              </button>
            </div>

            {/* Keep all panels mounted to avoid image reload/flicker */}
            <section
              id='panel-results'
              role='tabpanel'
              aria-labelledby='tab-results'
              hidden={activeTab !== TABS.RESULTS}
              aria-hidden={activeTab !== TABS.RESULTS}
            >
              {surveys.map((r, idx) => (
                <article
                  key={r.surveyResultId || r.responseId || idx}
                  className={`card ${idx % 2 === 1 ? 'alt' : ''}`}
                >
                  <div className='card-left'>
                    <img
                      className='card-avatar'
                      src={'/SurveyLogo.png'}
                      width={72}
                      height={72}
                      alt='Test icon'
                      loading='eager'
                      decoding='async'
                      fetchPriority='high'
                    />
                    <div>
                      <h2 className='card-title'>
                        The InnerScience Personality Test Result
                      </h2>
                      <div className='card-meta'>
                        {r.submittedAt
                          ? formatDateTime(new Date(r.submittedAt))
                          : ''}
                      </div>
                    </div>
                  </div>
                  <div className='cta-row'>
                    {r.unlocked ? (
                      <button
                        className='btn-pill'
                        type='button'
                        onClick={() => downloadPdf(r)}
                      >
                        <span className='chip'>
                          <LockIcon />
                        </span>
                        Download PDF
                      </button>
                    ) : (
                      <button
                        className='btn-pill'
                        type='button'
                        onClick={() =>
                          navigate('/survey-result', { state: { result: r } })
                        }
                      >
                        <span className='chip'>
                          <LockIcon />
                        </span>
                        Upgrade Report
                      </button>
                    )}
                  </div>
                </article>
              ))}
              {surveys.length === 0 && (
                <div
                  style={{
                    padding: '24px 32px',
                    color: 'var(--slate)',
                    fontFamily: 'var(--ui)',
                  }}
                >
                  No results yet.
                </div>
              )}
            </section>

            <section
              id='panel-account'
              role='tabpanel'
              aria-labelledby='tab-account'
              hidden={activeTab !== TABS.ACCOUNT}
              aria-hidden={activeTab !== TABS.ACCOUNT}
            >
              <div className='form-wrap'>
                <form className='form' onSubmit={handleSave}>
                  <div className='field'>
                    <label htmlFor='displayName'>Display name *</label>
                    <input
                      id='displayName'
                      className='input'
                      type='text'
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                    />
                    <p className='help'>
                      Your display name may be shown to other users, for
                      instance in comments and forum posts.
                    </p>
                  </div>

                  <div className='field field-current-password'>
                    <label htmlFor='currentPassword'>Current password</label>
                    <input
                      id='currentPassword'
                      className='input'
                      type='password'
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                    />
                    <p className='helper-line'>
                      Enter your current password to change the E-mail address
                      or Password.{' '}
                      <Link to='/forgot-password' state={{ email }}>
                        Request new password
                      </Link>
                      .
                    </p>
                  </div>

                  <div className='field'>
                    <label htmlFor='email'>E-mail address *</label>
                    <input
                      id='email'
                      className='input'
                      type='email'
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder='name@example.com'
                    />
                    <p className='help'>
                      Please enter a valid e-mail address. We have a zero-spam
                      policy.
                    </p>
                  </div>

                  <div className='field'>
                    <label htmlFor='password'>Password</label>
                    <input
                      id='password'
                      className='input'
                      type='password'
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                  </div>

                  <div className='field'>
                    <label htmlFor='confirmPassword'>Confirm password</label>
                    <input
                      id='confirmPassword'
                      className='input'
                      type='password'
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                  </div>

                  <button
                    className='save'
                    type='submit'
                    disabled={saveLoading}
                    aria-busy={saveLoading}
                  >
                    {saveLoading ? 'Saving...' : 'Save'}
                  </button>
                  {saveMsg && (
                    <p
                      className='help'
                      style={{ color: '#0C3C47', fontStyle: 'normal' }}
                    >
                      {saveMsg}
                    </p>
                  )}
                  {saveErr && (
                    <p
                      className='help'
                      style={{ color: '#b00020', fontStyle: 'normal' }}
                    >
                      {saveErr}
                    </p>
                  )}
                </form>
              </div>
            </section>

            <section
              id='panel-orders'
              role='tabpanel'
              aria-labelledby='tab-orders'
              hidden={activeTab !== TABS.ORDERS}
              aria-hidden={activeTab !== TABS.ORDERS}
            >
              {purchases.map((p, idx) => (
                <article
                  key={p.id || idx}
                  className={`card ${idx % 2 === 1 ? 'alt' : ''}`}
                >
                  <div className='card-left'>
                    <img
                      className='card-avatar'
                      src={'/OrderLogo.png'}
                      width={72}
                      height={72}
                      alt='Order icon'
                      loading='eager'
                      decoding='async'
                      fetchPriority='high'
                    />
                    <div>
                      <h2 className='card-title'>
                        Order — $
                        {p.price?.toFixed ? p.price.toFixed(2) : p.price}
                      </h2>
                      <div className='card-meta'>
                        {p.purchaseDate
                          ? formatDateTime(new Date(p.purchaseDate))
                          : ''}{' '}
                        · {p.status}
                      </div>
                    </div>
                  </div>
                  <div className='cta-row'>
                    {p.receiptUrl && (
                      <a
                        className='btn-pill'
                        href={p.receiptUrl}
                        target='_blank'
                        rel='noopener noreferrer'
                      >
                        View Receipt
                      </a>
                    )}
                  </div>
                </article>
              ))}
              {purchases.length === 0 && (
                <div
                  style={{
                    padding: '24px 32px',
                    color: 'var(--slate)',
                    fontFamily: 'var(--ui)',
                  }}
                >
                  No orders found.
                </div>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
}
